import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import LayoutSelector from 'components/Navbars/ServicePanel/ProtocolModal/components/LayoutSelector';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';
import { Button } from 'reactstrap';
import {
  DragHandle,
  DynamicLayoutContainer,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from './styled';

const Portal = ({ children }) => {
  const [mounted, setMounted] = useState(false);
  const portalRef = useRef(null);

  useEffect(() => {
    const portalRoot =
      document.getElementById('portal-root') ||
      (() => {
        const div = document.createElement('div');
        div.id = 'portal-root';
        document.body.appendChild(div);
        return div;
      })();

    const el = document.createElement('div');
    portalRoot.appendChild(el);
    portalRef.current = el;
    setMounted(true);

    return () => {
      if (portalRef.current) {
        portalRoot.removeChild(portalRef.current);
      }
    };
  }, []);

  return mounted && portalRef.current
    ? createPortal(children, portalRef.current)
    : null;
};

const throttle = (func, limit) => {
  let inThrottle;
  let lastArgs;

  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        if (lastArgs) {
          func.apply(this, lastArgs);
          lastArgs = null;
        }
        inThrottle = false;
      }, limit);
    } else {
      lastArgs = args;
    }
  };
};

export const DynamicLayoutModal = ({
  children,
  show,
  onModalToggle,
  title = '',
  subtitle = '',
  showCloseButton = true,
  backdrop = true,
  closeModal = null,
  hasDynamicLayout = false,
  modalWidth = 800,
  sidebarWidth = 300,
  minWidth = 300,
  maxWidth = '50vw',
  isToClickOutsideClose = true,
  ...props
}) => {
  const systemConfiguration = useSelector((state) => state.systemConfiguration);
  const [selectedLayout, setSelectedLayout] = useState(() => {
    return localStorage.getItem('selectedLayout') || 'modal';
  });
  const [modalCurrentWidth, setModalCurrentWidth] = useState(modalWidth);
  const [sidebarCurrentWidth, setSidebarCurrentWidth] = useState(sidebarWidth);
  const [width, setWidth] = useState(
    selectedLayout === 'modal' ? modalCurrentWidth : sidebarCurrentWidth
  );

  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const isResizingRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (selectedLayout === 'modal') {
      if (show) {
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.body.style.overflow = 'unset';
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [show, selectedLayout]);

  const handleLayoutChange = useCallback(
    (layout) => {
      setSelectedLayout(layout);
      setWidth(layout === 'modal' ? modalCurrentWidth : sidebarCurrentWidth);
      localStorage.setItem('selectedLayout', layout);
    },
    [modalCurrentWidth, sidebarCurrentWidth]
  );

  useEffect(() => {
    onModalToggle(show);
  }, [show, onModalToggle]);

  const handleClose = useCallback(() => {
    if (closeModal) {
      closeModal();
    }
  }, [closeModal]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (isToClickOutsideClose) {
        handleClose();
        return;
      }
      e.stopPropagation();
    },
    [isToClickOutsideClose, handleClose]
  );

  const handleMouseDown = useCallback(
    (e) => {
      if (selectedLayout === 'sidebar') {
        isResizingRef.current = true;
        startXRef.current = e.clientX;
        startWidthRef.current = width;
        document.body.classList.add('resize-active');
        e.preventDefault();
      }
    },
    [selectedLayout, width]
  );

  const handleMouseMove = useCallback(
    throttle((e) => {
      if (!isResizingRef.current) return;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        const deltaX = startXRef.current - e.clientX;
        const newWidth = Math.max(
          minWidth,
          Math.min(maxWidth, startWidthRef.current + deltaX)
        );

        if (containerRef.current) {
          containerRef.current.style.width = `${newWidth}px`;
        }
      });
    }, 16),
    [minWidth, maxWidth]
  );

  const handleMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;

    isResizingRef.current = false;
    document.body.classList.remove('resize-active');

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    if (containerRef.current) {
      const finalWidth = parseInt(containerRef.current.style.width, 10);
      if (selectedLayout === 'modal') {
        setModalCurrentWidth(finalWidth);
      } else {
        setSidebarCurrentWidth(finalWidth);
      }
      setWidth(finalWidth);
    }
  }, [selectedLayout]);

  useEffect(() => {
    if (!show) return;

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [show, handleMouseMove, handleMouseUp, handleClose]);

  useEffect(() => {
    const handleWheel = (e) => {
      if (selectedLayout === 'sidebar' && containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

        if (scrollHeight > clientHeight) {
          const isScrollingDown = e.deltaY > 0;
          const isScrollingUp = e.deltaY < 0;

          const isAtBottom = scrollTop + clientHeight >= scrollHeight;
          const isAtTop = scrollTop <= 0;

          if ((isScrollingDown && !isAtBottom) || (isScrollingUp && !isAtTop)) {
            e.preventDefault();
            containerRef.current.scrollTop += e.deltaY;
          }
        }
      }
    };
    document.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, [selectedLayout]);

  if (!show) return null;

  const content = (
    <>
      {backdrop && selectedLayout === 'modal' && (
        <ModalOverlay onClick={handleBackdropClick} />
      )}
      <DynamicLayoutContainer
        ref={containerRef}
        layout={selectedLayout}
        width={width}
        style={{ width: `${width}px` }}
        {...props}
      >
        <ModalHeader>
          <div>
            <h6 className="modal-title">{title}</h6>
            {subtitle && <p>{subtitle}</p>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center' }}>
            {hasDynamicLayout && (
              <LayoutSelector
                selectedLayout={selectedLayout}
                onLayoutChange={handleLayoutChange}
                systemConfiguration={systemConfiguration}
              />
            )}
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={handleClose}
              style={{ marginLeft: '10px' }}
            >
              <span aria-hidden={true}>×</span>
            </button>
          </div>
        </ModalHeader>

        <ModalBody>{children}</ModalBody>

        {showCloseButton && (
          <ModalFooter>
            <Button
              style={buttonStyle(systemConfiguration.cancelation_button_color)}
              onClick={handleClose}
            >
              Fechar
            </Button>
          </ModalFooter>
        )}

        <DragHandle layout={selectedLayout} onMouseDown={handleMouseDown} />
      </DynamicLayoutContainer>
    </>
  );

  return <Portal>{content}</Portal>;
};
